/**
 * @file Home.tsx
 * @description 首页 - 显示功能卡片网格
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card'
import { BookOpen, PlayCircle, Database, BrainCircuit, User, LogOut, FileText, Presentation, Shapes, Paintbrush, GraduationCap, Layers, KeyRound } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAuthStore } from '../store/authStore'
import { ROUTES } from '../constants/routes'
import { toast } from 'sonner'

const profileSchema = z.object({
  username: z.string().min(1, '请输入用户名'),
  email: z.string().email('请输入有效邮箱'),
  role: z.string().optional(),
  stage: z.string().optional(),
  grade: z.string().optional(),
})

type ProfileFormValues = z.infer<typeof profileSchema>

const passwordSchema = z.object({
  oldPassword: z.string().min(1, '请输入当前密码'),
  newPassword: z.string().min(6, '新密码至少6个字符'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: '两次输入的密码不一致',
  path: ['confirmPassword'],
})

type PasswordFormValues = z.infer<typeof passwordSchema>

export default function Home() {
  const navigate = useNavigate()
  const { user, logout, updateProfile, changePassword, initFromStorage } = useAuthStore()
  const isStudent = user?.role === 'student'
  const [open, setOpen] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      username: user?.username ?? '',
      email: user?.email ?? '',
      role: user?.role ?? '',
      stage: user?.stage ?? '',
      grade: user?.grade ?? '',
    },
  })

  const {
    register: registerPwd,
    handleSubmit: handlePwdSubmit,
    reset: resetPwd,
    formState: { errors: pwdErrors, isSubmitting: isPwdSubmitting },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const stage = watch('stage')

  const allGradeOptions = [
    '一年级',
    '二年级',
    '三年级',
    '四年级',
    '五年级',
    '六年级',
    '初一',
    '初二',
    '初三',
    '高一',
    '高二',
    '高三',
    '大学',
  ]

  const getGradesByStage = (stageValue: string | undefined) => {
    switch (stageValue) {
      case '小学':
        return allGradeOptions.slice(0, 6)
      case '初中':
        return allGradeOptions.slice(6, 9)
      case '高中':
        return allGradeOptions.slice(9, 12)
      case '大学':
        return ['大学']
      default:
        return allGradeOptions
    }
  }

  const gradeOptions = getGradesByStage(stage)

  useEffect(() => {
    if (!user) {
      initFromStorage()
    }
  }, [user, initFromStorage])

  useEffect(() => {
    if (user) {
      reset({
        username: user.username,
        email: user.email,
        role: user.role ?? '',
        stage: user.stage ?? '',
        grade: user.grade ?? '',
      })
    }
  }, [user, reset])

  const onOpenSettings = () => {
    if (!user) {
      navigate(ROUTES.LOGIN)
      return
    }
    setOpen(true)
  }

  const onSubmit = async (values: ProfileFormValues) => {
    try {
      await updateProfile({
        username: values.username,
        email: values.email,
        role: values.role || undefined,
        stage: values.stage || undefined,
        grade: values.grade || undefined,
      })
      setOpen(false)
    } catch {
    }
  }

  const onPasswordSubmit = async (values: PasswordFormValues) => {
    try {
      await changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      })
      toast.success('密码修改成功')
      setPasswordSuccess(true)
      resetPwd()
      setTimeout(() => {
        setShowPasswordForm(false)
        setPasswordSuccess(false)
      }, 1500)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '修改密码失败')
    }
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {!isStudent && (
          <Link to={ROUTES.TEACHER} className="group">
            <Card className="h-full hover:shadow-lg transition-all border-blue-100 hover:border-blue-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <BookOpen className="w-5 h-5" />
                  备课工作台
                </CardTitle>
                <CardDescription>Teacher Workspace</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  设计课程结构，编辑知识点，生成教学脚本。
                </p>
              </CardContent>
            </Card>
          </Link>
        )}

        {!isStudent && (
          <Link to={ROUTES.STUDENT} className="group">
            <Card className="h-full hover:shadow-lg transition-all border-green-100 hover:border-green-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <PlayCircle className="w-5 h-5" />
                  学生听课端
                </CardTitle>
                <CardDescription>Student Player</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  沉浸式学习体验，包含语音讲解与互动练习。
                </p>
              </CardContent>
            </Card>
          </Link>
        )}

        <Link to={ROUTES.MISTAKES} className="group">
          <Card className="h-full hover:shadow-lg transition-all border-rose-100 hover:border-rose-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-rose-700">
                <BookOpen className="w-5 h-5" />
                错题本
              </CardTitle>
              <CardDescription>Mistake Book</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                聚合错题与复盘记录，支持状态管理与练习。
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to={ROUTES.FLASHCARDS} className="group">
          <Card className="h-full hover:shadow-lg transition-all border-sky-100 hover:border-sky-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sky-700">
                <Layers className="w-5 h-5" />
                闪卡
              </CardTitle>
              <CardDescription>Flashcards</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                间隔重复记忆卡片，支持知识点/错题自动生成。
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to={ROUTES.AI_TEAM} className="group">
          <Card className="h-full hover:shadow-lg transition-all border-violet-100 hover:border-violet-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-violet-700">
                <GraduationCap className="w-5 h-5" />
                AI 学习中心
              </CardTitle>
              <CardDescription>AI Learning Center</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                你的专属 AI 辅导团队：学习诊断、错题分析、个性化课程。
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to={ROUTES.EXAM} className="group">
          <Card className="h-full hover:shadow-lg transition-all border-indigo-100 hover:border-indigo-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-indigo-700">
                <FileText className="w-5 h-5" />
                真题练习
              </CardTitle>
              <CardDescription>Exam Practice</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                进入真题练习，完成训练与成绩评估。
              </p>
            </CardContent>
          </Card>
        </Link>

        {!isStudent && (
          <Link to={ROUTES.QB_EXTRACT} className="group">
            <Card className="h-full hover:shadow-lg transition-all border-purple-100 hover:border-purple-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  <Database className="w-5 h-5" />
                  题库录入
                </CardTitle>
                <CardDescription>Question Extraction</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  上传试题图片，自动OCR与结构化提取。
                </p>
              </CardContent>
            </Card>
          </Link>
        )}

        {!isStudent && (
          <Link to={ROUTES.TUTOR} className="group">
            <Card className="h-full hover:shadow-lg transition-all border-amber-100 hover:border-amber-300 ring-2 ring-amber-500 ring-offset-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <BrainCircuit className="w-5 h-5" />
                  AI 试题讲解 (MVP)
                </CardTitle>
                <CardDescription>AI Math Tutor</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  三栏沉浸式讲解，分步引导 + 智能纠错。
                </p>
              </CardContent>
            </Card>
          </Link>
        )}
        {!isStudent && (
          <Link to={ROUTES.TUTOR_VISUALIZATION} className="group">
            <Card className="h-full hover:shadow-lg transition-all border-cyan-100 hover:border-cyan-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-cyan-700">
                  <Presentation className="w-5 h-5" />
                  解题过程图示
                </CardTitle>
                <CardDescription>Math Visualization</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  左侧主画布 + 右侧步骤导航，专注分步图示与动画播放。
                </p>
              </CardContent>
            </Card>
          </Link>
        )}
        {!isStudent && (
          <Link to={ROUTES.GEOMASTER} className="group">
            <Card className="h-full hover:shadow-lg transition-all border-teal-100 hover:border-teal-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-teal-700">
                  <Shapes className="w-5 h-5" />
                  Geomaster Studio
                </CardTitle>
                <CardDescription>Geometry DSL Workspace</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  一体化输入、生成、校验与调试，快速产出几何 DSL。
                </p>
              </CardContent>
            </Card>
          </Link>
        )}

        {!isStudent && (
          <Link to={ROUTES.XIAOHUA} className="group">
            <Card className="h-full hover:shadow-lg transition-all border-amber-100 hover:border-amber-300 ring-2 ring-amber-500 ring-offset-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <Paintbrush className="w-5 h-5" />
                  小画老师
                </CardTitle>
                <CardDescription>SVG 图解数学</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  用画图的方式讲解小学应用题，适合 1-4 年级学生。
                </p>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => {
        setOpen(v)
        if (!v) {
          setShowPasswordForm(false)
          setPasswordSuccess(false)
          resetPwd()
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>个人设置</DialogTitle>
          </DialogHeader>

          {!showPasswordForm ? (
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-1">
                <label className="text-sm">用户名</label>
                <Input {...register('username')} />
                {errors.username && (
                  <p className="text-xs text-red-500">{errors.username.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-sm">邮箱</label>
                <Input {...register('email')} />
                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-sm">角色</label>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
                  {...register('role')}
                >
                  <option value="">未设置</option>
                  <option value="teacher">教师</option>
                  <option value="student">学生</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm">学段</label>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
                  {...register('stage')}
                >
                  <option value="">未设置</option>
                  <option value="小学">小学</option>
                  <option value="初中">初中</option>
                  <option value="高中">高中</option>
                  <option value="大学">大学</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm">年级</label>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
                  {...register('grade')}
                  disabled={!stage}
                >
                  <option value="">未设置</option>
                  {gradeOptions.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between items-center pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-sm text-slate-500 hover:text-slate-700"
                  onClick={() => setShowPasswordForm(true)}
                >
                  <KeyRound className="w-4 h-4 mr-1" />
                  修改密码
                </Button>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setOpen(false)}
                  >
                    取消
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    保存
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={handlePwdSubmit(onPasswordSubmit)}>
              <p className="text-sm text-slate-500 mb-2">修改密码后，下次登录请使用新密码。</p>
              <div className="space-y-1">
                <label className="text-sm">当前密码</label>
                <Input type="password" {...registerPwd('oldPassword')} placeholder="请输入当前密码" />
                {pwdErrors.oldPassword && (
                  <p className="text-xs text-red-500">{pwdErrors.oldPassword.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-sm">新密码</label>
                <Input type="password" {...registerPwd('newPassword')} placeholder="至少6个字符" />
                {pwdErrors.newPassword && (
                  <p className="text-xs text-red-500">{pwdErrors.newPassword.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-sm">确认新密码</label>
                <Input type="password" {...registerPwd('confirmPassword')} placeholder="再次输入新密码" />
                {pwdErrors.confirmPassword && (
                  <p className="text-xs text-red-500">{pwdErrors.confirmPassword.message}</p>
                )}
              </div>
              {passwordSuccess && (
                <p className="text-sm text-green-600">密码修改成功！</p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordForm(false)
                    setPasswordSuccess(false)
                    resetPwd()
                  }}
                >
                  返回
                </Button>
                <Button type="submit" disabled={isPwdSubmitting || passwordSuccess}>
                  确认修改
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
